import { createRouter, createWebHashHistory } from "vue-router";
import isAuthenticated from "../auth/authGuard";
import isAuthenticatedLogin from "../auth/authLogin";
import isAuthType from "../auth/authGuardAccess";
import axios from 'axios';
import { getToken, isExpired } from '../auth/token.js'; // adjust relative path
import Api from "../api/Api.js"

const PUBLIC_PATHS = new Set([
    '/login',
    '/reset-password',
    '/forgot-password',
]);

const routes = [
    {
        path: "/login",
        name: "Login",
        beforeEnter: [isAuthenticatedLogin],
        component: () => import("../users/views/Login"),
        meta: {
            hideNavbar: true,
        },
    },
    {
        path: "/home",
        name: "Home",
        beforeEnter: [isAuthenticated],
        component: () => import("../users/views/MenuIcons"),
    },
    {
        path: "/reports/menu",
        name: "ReportsMenu",
        beforeEnter: [isAuthenticated],
        component: () => import("../reports/views/MenuReports"),
    },
    {
        path: "/reports/bi",
        name: "BusinessInteligenceMenu",
        beforeEnter: [isAuthenticated],
        component: () => import("../reports/views/BusinessInteligenceMenu.vue"),
    },
    {
        path: "/reports/powerbi/1",
        name: "PowerBIReport1",
        beforeEnter: [isAuthenticated],
        component: () => import("../reports/views/PowerBIReport1.vue"),
    },
    {
        path: "/reports/powerbi/2",
        name: "PowerBIReport2",
        beforeEnter: [isAuthenticated],
        component: () => import("../reports/views/PowerBIReport2.vue"),
    },
    {
        path: "/reports/powerbi/3",
        name: "PowerBIReport3",
        beforeEnter: [isAuthenticated],
        component: () => import("../reports/views/PowerBIReport3.vue"),
    },
    {
        path: "/reports/powerbi/4",
        name: "PowerBIReport4",
        beforeEnter: [isAuthenticated],
        component: () => import("../reports/views/PowerBIReport4.vue"),
    },
    {
        path: "/contract",
        name: "Contract",
        beforeEnter: [isAuthenticated],
        component: () => import("../users/views/Contract"),
        meta: {
            hideNavbar: true,
        },
    },
    {
        path: "/locked",
        name: "Locked",
        component: () => import("../users/views/Locked"),
        meta: {
            hideNavbar: true,
        },
    },
    {
        path: "/reset-password",
        name: "ResetPassword",
        component: () => import("../users/views/ResetPassword"),
        meta: {
            hideNavbar: true,
        },
    },
    {
        path: "/forgot-password",
        name: "RequestPasswordReset",
        component: () => import("../users/views/RequestPasswordReset"),
        meta: {
            hideNavbar: true,
        },
    },
    {
        path: "/config",
        name: "Configuration",
        beforeEnter: [isAuthenticated],
        component: () => import("../management/SystemConfiguration.vue"),
    },
    {
        // Megaadmin-only module switches. Guard is auth-only; the view redirects non-megaadmins and
        // the backend (settings/modules) enforces req.isMega — this route just hides the entry point.
        path: "/config/modules",
        name: "ModulesConfig",
        beforeEnter: [isAuthenticated],
        component: () => import("../management/ModulesConfig.vue"),
    },
    {
        // Megaadmin-only BI dashboard builder. Guard is auth-only; the view redirects non-megaadmins
        // and the backend (/dashboards/*) enforces req.isMega.
        path: "/config/dashboards",
        name: "DashboardBuilder",
        beforeEnter: [isAuthenticated],
        component: () => import("../management/DashboardBuilder.vue"),
    },
    {
        // Published BI dashboard, viewed by tenant superadmins. Backend gates by the `dashboards`
        // module + reports access.
        path: "/dashboards/:key",
        name: "DynamicDashboard",
        beforeEnter: [isAuthenticated],
        component: () => import("../reports/views/DynamicDashboard.vue"),
    },
    {
        path: "/users/alta",
        name: "CreateUser",
        beforeEnter: [isAuthType],
        component: () => import("../users/components/Register"),
    },
    {
        path: "/users",
        name: "Users",
        beforeEnter: [isAuthenticated],
        component: () => import("../users/views/Users"),
    },
    {
        path: "/users/:id",
        name: "User",
        beforeEnter: [isAuthenticated],
        component: () => import("../users/views/Profile"),
        props: (route) => {
            const id = Number(route.params.id);
            return isNaN(id)
                ? { id: localStorage.getItem("idSearch") }
                : { id };
        },
    },
    {
        path: "/users/:id/incentives",
        name: "UserIncentives",
        beforeEnter: [isAuthenticated],
        component: () => import("../users/views/UserIncentives"),
        props: (route) => {
            const id = Number(route.params.id);
            return isNaN(id)
                ? { id: localStorage.getItem("idSearch") }
                : { id };
        },
    },
    {
        path: "/user/clients",
        name: "SellerClients",
        beforeEnter: [isAuthenticated],
        component: () => import("../sellers/views/Clients"),
    },
    {
        path: "/clients",
        name: "Clients",
        beforeEnter: [isAuthType],
        component: () => import("../clients/views/Clients"),
    },
    {
        path: "/segmentos",
        name: "ClientTypes",
        beforeEnter: [isAuthType],
        component: () => import("../clients/views/Types"),
    },
    {
        path: "/clientes/registro",
        name: "RegisterClient",
        beforeEnter: [isAuthType],
        component: () => import("../clients/components/Register"),
    },
    {
        path: "/clients/prospects",
        name: "ClientsProspects",
        beforeEnter: [isAuthType],
        component: () => import("../clients/views/Prospects"),
    },
    {
        path: "/sales-funnel",
        name: "SalesFunnel",
        beforeEnter: [isAuthenticated],
        component: () => import("../sales_funnel/views/SalesFunnel"),
    },
    {
        path: "/clients/:id",
        name: "ClientProfile",
        beforeEnter: [isAuthType],
        component: () => import("../clients/components/Client"),
        props: (route) => {
            const id = Number(route.params.id);
            return isNaN(id)
                ? { id: localStorage.getItem("client_id") }
                : { id };
        },
    },
    {
        path: "/clients/:id/incentives",
        name: "ClientIncentives",
        beforeEnter: [isAuthType],
        component: () => import("../clients/views/ClientIncentives"),
        props: (route) => {
            const id = Number(route.params.id);
            return { id };
        },
    },
    {
        path: "/clients/groups",
        name: "Groups",
        beforeEnter: [isAuthType],
        component: () => import("../clients/views/Groups"),
    },
    {
        path: "/clients/map",
        name: "ClientsSalesMap",
        beforeEnter: [isAuthType],
        component: () => import("../clients/views/ClientsSalesMap"),
    },
    {
        path: "/rangos",
        name: "Ranges",
        beforeEnter: [isAuthType],
        component: () => import("../prices/Prices"),
    },
    {
        path: "/ranges/:range_id",
        name: "Range",
        beforeEnter: [isAuthType],
        component: () => import("../prices/Price.vue"),
        props: (route) => {
            const range_id = Number(route.params.range_id);
            return { range_id };
        },
    },
    {
        path: "/groups/:id",
        name: "Group",
        beforeEnter: [isAuthType],
        component: () => import("../clients/views/Group.vue"),
        props: (route) => {
            return { group_id: route.params.id };
        },
    },
    {
        path: "/routes",
        name: "Routes",
        beforeEnter: [isAuthType],
        component: () => import("../client_routes/views/Routes"),
    },
    {
        path: "/routes/:route_id",
        name: "Route",
        beforeEnter: [isAuthType],
        component: () => import("../client_routes/views/Route"),
        props: (route) => {
            const route_id = Number(route.params.route_id);
            return { route_id };
        },
    },
    {
        path: "/clients/orders",
        name: "ClientsOrders",
        beforeEnter: [isAuthType],
        component: () => import("../sellers/views/ClientsOrders"),
    },
    {
        path: "/clients/orders/:order_id",
        name: "ClientOrderView",
        beforeEnter: [isAuthType],
        component: () => import("../sellers/views/ClientOrderView"),
        props: (route) => {
            const order_id = Number(route.params.order_id);
            return { order_id };
        },
    },
    {
        path: "/clientes/ventas",
        name: "Sales",
        beforeEnter: [isAuthType],
        component: () => import("../clients/views/Sales"),
    },
    {
        path: "/clients/order/create/:business_id?",
        name: "CreateOrder",
        beforeEnter: [isAuthType],
        component: () => import("../sellers/views/CreateOrder"),
        props: (route) => {
            const business_id = Number(route.params.business_id);
            return { business_id: Number.isNaN(business_id) ? null : business_id };
        },
    },
    {
        path: "/products",
        name: "Products",
        beforeEnter: [isAuthenticated],
        component: () => import("../products/views/Products"),
    },
    {
        path: "/products/price-list",
        name: "PriceList",
        beforeEnter: [isAuthenticated],
        component: () => import("../products/views/PriceList"),
    },
    {
        path: "/onboarding-import",
        name: "OnboardingImport",
        beforeEnter: [isAuthenticated],
        component: () => import("../products/views/OnboardingImport"),
    },
    {
        path: "/presentaciones",
        name: "ProductUnits",
        beforeEnter: [isAuthenticated],
        component: () => import("../products/views/Units"),
    },
    {
        path: "/Productos/Lineas-de-producto",
        name: "ProductTypes",
        beforeEnter: [isAuthenticated],
        component: () => import("../products/views/Types"),
    },
    {
        path: "/Productos/Datos-de-producto",
        name: "ProductMetadata",
        beforeEnter: [isAuthenticated],
        component: () => import("../products/views/Metadata"),
    },
    {
        path: "/admin/promotions/",
        name: "Promotions",
        beforeEnter: [isAuthenticated],
        component: () => import("../promotions/Promotions"),
    },
    {
        path: "/admin/promotions/create",
        name: "CreatePromotions",
        beforeEnter: [isAuthenticated],
        component: () => import("../promotions/createPromotion"),
    },
    {
        path: "/admin/promotions/list",
        name: "ListPromotions",
        beforeEnter: [isAuthenticated],
        component: () => import("../promotions/listPromotions"),
    },
    {
        path: "/products/alta",
        name: "AltaProduct",
        beforeEnter: [isAuthenticated],
        component: () => import("../products/components/Register"),
    },
    {
        path: "/products/:id",
        name: "Producto",
        beforeEnter: [isAuthenticated],
        component: () => import("../products/views/Product"),
        props: (route) => {
            const id = Number(route.params.id);
            return isNaN(id)
                ? { id: localStorage.getItem("product_id") }
                : { id };
        },
    },
    {
        path: "/almacenes",
        name: "Warehouses",
        beforeEnter: [isAuthenticated],
        component: () => import("../warehouses/views/Warehouses"),
    },
    {
        path: "/almacenes/:id",
        name: "Almacen",
        beforeEnter: [isAuthenticated],
        component: () => import("../warehouses/views/Warehouse"),
        props: (route) => {
            const id = Number(route.params.id);
            return isNaN(id)
                ? { id: localStorage.getItem("warehouse_id") }
                : { id };
        },
    },
    {
        path: "/almacenes/alta",
        name: "AltaAlmacenes",
        beforeEnter: [isAuthenticated],
        component: () => import("../warehouses/components/Register"),
    },
    {
        path: "/almacenes/secciones/info",
        name: "SeccionInfo",
        beforeEnter: [isAuthenticated],
        component: () => import("../warehouses/components/SectionContent"),
    },
    {
        path: "/almacenes/reporte",
        name: "InventoryReportBi",
        beforeEnter: [isAuthenticated],
        component: () => import("../reports/views/InventoryReportBi"),
    },
    {
        path: "/reportes/inventario",
        name: "InventoryReport",
        beforeEnter: [isAuthenticated],
        component: () => import("../reports/views/InventoryReport"),
    },
    {
        path: "/reportes/ventas",
        name: "SalesReport",
        beforeEnter: [isAuthenticated],
        component: () => import("../reports/views/SalesReport"),
    },
    {
        path: "/manufactura",
        name: "ManufacturingMenu",
        beforeEnter: [isAuthenticated],
        component: () => import("../manufacturing/views/ManufacturingMenu"),
    },
    {
        path: "/manufactura/recetas",
        name: "Recipes",
        beforeEnter: [isAuthenticated],
        component: () => import("../manufacturing/views/Recipes"),
    },
    {
        path: "/manufactura/recetas/nueva",
        name: "RecipeCreate",
        beforeEnter: [isAuthenticated],
        component: () => import("../manufacturing/views/RecipeEdit"),
    },
    {
        path: "/manufactura/recetas/:recipe_id",
        name: "RecipeEdit",
        beforeEnter: [isAuthenticated],
        component: () => import("../manufacturing/views/RecipeEdit"),
    },
    {
        path: "/manufactura/ordenes",
        name: "ProductionOrders",
        beforeEnter: [isAuthenticated],
        component: () => import("../manufacturing/views/ProductionOrders"),
    },
    {
        path: "/manufactura/cotizaciones",
        name: "Quotes",
        beforeEnter: [isAuthenticated],
        component: () => import("../manufacturing/views/Quotes"),
    },
    {
        path: "/manufactura/lineas",
        name: "ProductionLines",
        beforeEnter: [isAuthenticated],
        component: () => import("../manufacturing/views/ProductionLines"),
    },
    {
        path: "/manufactura/materias-primas",
        name: "RawMaterials",
        beforeEnter: [isAuthenticated],
        component: () => import("../manufacturing/views/RawMaterials"),
    },
    {
        path: "/manufactura/ejecucion/:production_order_id",
        name: "ProductionExecution",
        beforeEnter: [isAuthenticated],
        component: () => import("../manufacturing/views/ProductionExecution"),
    },
    {
        path: "/manufactura/tablero",
        name: "ManufacturingDashboard",
        beforeEnter: [isAuthenticated],
        component: () => import("../manufacturing/views/ManufacturingDashboard"),
    },
    {
        path: "/manufactura/configuracion-costos",
        name: "ManufacturingCostConfig",
        beforeEnter: [isAuthenticated],
        component: () => import("../manufacturing/views/ManufacturingCostConfig"),
    },
    {
        path: "/manufactura/display",
        name: "FloorDisplay",
        beforeEnter: [isAuthenticated],
        component: () => import("../manufacturing/views/FloorDisplay"),
        meta: {
            hideNavbar: true,
        },
    },
    {
        path: "/proveedores",
        name: "Suppliers",
        beforeEnter: [isAuthenticated],
        component: () => import("../suppliers/views/Suppliers"),
    },
    {
        path: "/proveedores/registro",
        name: "SupplierRegister",
        beforeEnter: [isAuthenticated],
        component: () => import("../suppliers/components/Register"),
    },
    {
        path: "/proveedor/:id",
        name: "Supplier",
        beforeEnter: [isAuthenticated],
        component: () => import("../suppliers/views/Supplier"),
        props: (route) => {
            const id = Number(route.params.id);
            return isNaN(id)
                ? { id: localStorage.getItem("supplierId") }
                : { id };
        },
    },
    {
        path: "/ordenes",
        name: "Orders",
        beforeEnter: [isAuthenticated],
        component: () => import("../orders/views/Orders"),
    },
    {
        path: "/ordenes/:id",
        name: "OrderView",
        beforeEnter: [isAuthenticated],
        component: () => import("../orders/components/Order"),
        props: (route) => {
            const id = Number(route.params.id);
            return isNaN(id)
                ? { id: localStorage.getItem("order_id") }
                : { id };
        },
    },
    {
        path: "/repartos",
        name: "Runs",
        beforeEnter: [isAuthenticated],
        component: () => import("../runs/views/Runs.vue"),
    },
    {
        path: "/repartos/:run_id",
        name: "Run",
        beforeEnter: [isAuthenticated],
        component: () => import("../runs/views/RunView.vue"),
        props: (route) => {
            const run_id = Number(route.params.run_id);
            return { run_id };
        },
    },
    {
        path: "/repartos/crear/:route_id",
        name: "CreateRun",
        beforeEnter: [isAuthenticated],
        component: () => import("../runs/views/CreateRun.vue"),
        props: (route) => {
            return { route_id: route.params.route_id };
        },
    },
    {
        path: "/reportes/clientes",
        name: "ClientsReport",
        beforeEnter: [isAuthenticated],
        component: () => import("../reports/views/ClientsReport.vue"),
    },
    {
        path: "/vendedores/deuda",
        name: "SellerDebt",
        beforeEnter: [isAuthenticated],
        component: () => import("../sellers/views/Debt.vue"),
    },
    {
        path: "/vendedores/comisiones",
        name: "Comissions",
        beforeEnter: [isAuthenticated],
        component: () => import("../sellers/views/Comissions.vue"),
    },
    {
        path: "/cobranza",
        name: "DebtReport",
        beforeEnter: [isAuthenticated],
        component: () => import("../sellers/views/DebtReport.vue"),
    },
    {
        path: "/clientes/enfoque",
        name: "ClientFocus",
        beforeEnter: [isAuthenticated],
        component: () => import("../sellers/views/ClientFocus.vue"),
    },
    {
        path: "/cobranza/pedidos",
        name: "OrdersDebt",
        beforeEnter: [isAuthenticated],
        component: () => import("../sellers/views/OrdersDebtReport.vue"),
    },
    {
        path: "/reportes/tridimensional",
        name: "TridimensionalReport",
        beforeEnter: [isAuthenticated],
        component: () => import("../reports/views/TridimensionalReport.vue"),
    },
    {
        path: "/admin/expenses",
        name: "Expenses",
        beforeEnter: [isAuthenticated],
        component: () => import("../management/Expenses.vue"),
    },
    {
        path: "/reportes/pagos",
        name: "PaymentsReport",
        beforeEnter: [isAuthenticated],
        component: () => import("../reports/views/PaymentsReport.vue"),
    },
    {
        path: "/reportes/conciliacion",
        name: "PaymentsReconciliationReport",
        beforeEnter: [isAuthenticated],
        component: () => import("../reports/views/PaymentsReconciliationReport.vue"),
    },
    {
        path: "/reportes/contabilidad-facturas",
        name: "AccountingInvoicesReport",
        beforeEnter: [isAuthenticated],
        component: () => import("../reports/views/AccountingInvoicesReport.vue"),
    },
    {
        path: "/contabilidad",
        name: "AccountingModule",
        beforeEnter: [isAuthenticated],
        component: () => import("../accounting/views/AccountingModule.vue"),
    },
    {
        path: "/gastos/categorias",
        name: "ExpenseCategories",
        beforeEnter: [isAuthenticated],
        component: () => import("../management/ExpenseCategories.vue"),
    },
    {
        path: "/dashboard",
        name: "Dashboard",
        beforeEnter: [isAuthenticated],
        component: () => import("../reports/views/Dashboard"),
    },
    {
        path: "/tutoriales",
        name: "Tutorials",
        beforeEnter: [isAuthenticated],
        component: () => import("../users/views/Tutorials"),
    },
    {
        path: "/:pathMatch(.*)*",
        beforeEnter: [isAuthenticated],
        component: () => import("../users/views/Home"),
    },
    {
        path: "/admin/conciliador",
        name: "MultiPaymentProcessor",
        beforeEnter: [isAuthenticated],
        component: () => import("../orders/views/MultiPaymentProcessor.vue"),
    },
    {
        path: "/home/menu",
        name: "MenuIcons",
        beforeEnter: [isAuthenticated],
        component: () => import("../users/views/MenuIcons.vue"),
    },
];

const router = createRouter({
    history: createWebHashHistory(),
    routes,
    scrollBehavior() {
        document.getElementById("app").scrollIntoView({ behavior: "smooth" });
    },
});

function setAuthHeader(token) {
  if (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common.Authorization;
  }
}

router.beforeEach(async (to) => {
  // 1) Public routes are always allowed
  if (PUBLIC_PATHS.has(to.path)) {
    return true;
  }

    // console.log('Navigating to:', to.fullPath);
  // 2) Must have a token
  const token = getToken();
  if (!token) {
    return { path: '/login', query: { redirect: to.fullPath } };
  }

    // console.log('Token found:', token);
    
  // 3) Token must not be expired (treat as expired 5s before)
  if (isExpired(token, 5000)) {
    // (Optional) Try one silent refresh before logging out
    try {
      const { data } = await Api.refresh();
      const newToken = data?.accessToken;
      if (!newToken) throw new Error('No accessToken from refresh');
      localStorage.setItem('apiKey', newToken);
      setAuthHeader(newToken);
      return true;
    } catch {
      localStorage.removeItem('apiKey');
      setAuthHeader(null);
      return { path: '/login', query: { redirect: to.fullPath } };
    }
  }

  // 4) Token OK → set header and continue
  setAuthHeader(token);
  return true;
});


export default router;
